import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Mail, ShoppingBag, Ticket, Users } from 'lucide-react';

const universeItems = [
  {
    id: 'merch',
    title: 'MERCH',
    subtitle: 'Coming Soon',
    description: 'Luxury apparel. Italian design. Limited drops.',
    icon: ShoppingBag,
    status: 'soon',
  },
  {
    id: 'experiences',
    title: 'EXPERIENCES',
    subtitle: 'Exclusive Events',
    description: 'Private listening sessions. Intimate concerts.',
    icon: Ticket,
    status: 'active',
  },
  {
    id: 'community',
    title: 'INNER CIRCLE',
    subtitle: 'Join Now',
    description: 'First access. Behind the scenes. The family.',
    icon: Users,
    status: 'active',
  },
];

export const Universe = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  return (
    <section id="universe" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-charcoal to-background" />
      <div className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-[10px] font-body uppercase tracking-[0.4em] text-crimson mb-4 block">
            Beyond Music
          </span>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-ivory">
            THE UNIVERSE
          </h2>
          <p className="mt-6 text-base text-ivory/50 max-w-lg mx-auto font-body tracking-wide">
            More than sound. A lifestyle. A statement.
          </p>
        </motion.div>

        {/* Universe Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {universeItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="group relative"
            >
              <div className="relative border border-border/30 bg-charcoal/30 p-8 h-full hover:border-crimson/30 hover:bg-charcoal/50 transition-all duration-400">
                {/* Icon */}
                <div className="w-12 h-12 border border-crimson/30 flex items-center justify-center mb-6 text-crimson group-hover:bg-crimson/10 transition-colors">
                  <item.icon className="w-5 h-5" />
                </div>

                {/* Status Badge */}
                {item.status === 'soon' && (
                  <span className="absolute top-4 right-4 px-2 py-1 text-[10px] uppercase tracking-wider bg-wine/50 text-ivory/70">
                    Soon
                  </span>
                )}

                <span className="text-[10px] uppercase tracking-[0.2em] text-crimson font-body">
                  {item.subtitle}
                </span>
                <h3 className="font-display text-2xl text-ivory mt-2 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-ivory/50 font-body mb-6">
                  {item.description}
                </p>

                <Button
                  variant={item.status === 'soon' ? 'minimal' : 'neonGhost'}
                  size="sm"
                  disabled={item.status === 'soon'}
                >
                  {item.status === 'soon' ? 'Notify Me' : 'Explore'}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>

                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-crimson/20" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-crimson/20" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="relative border border-crimson/20 bg-charcoal/30 p-8 sm:p-12 lg:p-16">
            {/* Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-crimson/10 blur-[100px]" />

            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <div className="w-14 h-14 border border-crimson/30 flex items-center justify-center mx-auto mb-6 text-crimson">
                <Mail className="w-6 h-6" />
              </div>
              
              <h3 className="font-display text-3xl sm:text-4xl text-ivory mb-4">
                JOIN THE INNER CIRCLE
              </h3>
              <p className="text-ivory/50 font-body mb-8">
                Exclusive drops. First access. Behind-the-scenes.
              </p>

              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-6 py-4 bg-muted/30 border border-border/30 text-ivory placeholder:text-muted-foreground font-body focus:outline-none focus:border-crimson/50 transition-colors"
                />
                <Button type="submit" variant="neon" size="lg">
                  {isSubscribed ? 'Welcome!' : 'Subscribe'}
                </Button>
              </form>

              <p className="text-[10px] text-muted-foreground mt-4 font-body tracking-wide">
                No spam. Only the essential. Unsubscribe anytime.
              </p>
            </div>

            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12 border-t border-l border-crimson/30" />
            <div className="absolute top-0 right-0 w-12 h-12 border-t border-r border-crimson/30" />
            <div className="absolute bottom-0 left-0 w-12 h-12 border-b border-l border-crimson/30" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-b border-r border-crimson/30" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
